import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/dashboard/Layout';
import { analyticsAPI, financialAPI, aiAPI } from '../services/api';
import {
  Laptop,
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const DashboardPage = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [analyticsRes, financialRes, anomaliesRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        financialAPI.getDashboard(),
        aiAPI.detectAnomalies().catch(() => ({ data: { anomalies: [] } }))
      ]);

      setMetrics(analyticsRes.data);
      setFinancial(financialRes.data);
      setAnomalies(anomaliesRes.data.anomalies || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-primary text-xl mono">Loading dashboard...</div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    {
      title: 'Total Assets',
      value: metrics?.assets?.total || 0,
      subtitle: `${metrics?.assets?.available || 0} available`,
      icon: Laptop,
      color: 'primary',
      testId: 'stat-total-assets'
    },
    {
      title: 'Active Rentals',
      value: metrics?.rentals?.active || 0,
      subtitle: `${metrics?.assets?.utilization_rate?.toFixed(1) || 0}% utilization`,
      icon: FileText,
      color: 'secondary',
      testId: 'stat-active-rentals'
    },
    {
      title: 'Total Clients',
      value: metrics?.clients?.total || 0,
      subtitle: `${metrics?.clients?.corporate || 0} corporate`,
      icon: Users,
      color: 'primary',
      testId: 'stat-total-clients'
    },
    {
      title: 'Monthly Revenue',
      value: `Rp ${((financial?.monthly_revenue || 0) / 1000000).toFixed(1)}M`,
      subtitle: 'This month',
      icon: DollarSign,
      color: 'secondary',
      testId: 'stat-monthly-revenue'
    }
  ];

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{letterSpacing: '-0.02em'}}>
              Control Center
            </h1>
            <p className="text-muted-foreground">Real-time system overview and analytics</p>
          </div>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary/10 text-primary border border-primary/30 rounded-lg hover:bg-primary/20 transition-colors btn"
            data-testid="refresh-dashboard"
          >
            Refresh
          </button>
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.title}
                className="stat-card glass-card p-6 rounded-xl border border-border/40"
                data-testid={stat.testId}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold metric-value text-foreground">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg bg-${stat.color}/10 border border-${stat.color}/30`}>
                    <Icon className={`w-6 h-6 text-${stat.color}`} />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
              </div>
            );
          })}
        </div>

        {/* Financial Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="glass-card p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Cash Position</h3>
              <TrendingUp className="w-5 h-5 text-secondary" />
            </div>
            <p className="text-3xl font-bold metric-value text-foreground mb-2">
              Rp {((financial?.cash_position || 0) / 1000000).toFixed(1)}M
            </p>
            <p className="text-sm text-muted-foreground">Available funds</p>
          </div>

          <div className="glass-card p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Outstanding</h3>
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <p className="text-3xl font-bold metric-value text-foreground mb-2">
              Rp {((financial?.total_outstanding || 0) / 1000000).toFixed(1)}M
            </p>
            <p className="text-sm text-muted-foreground">
              {financial?.overdue_count || 0} overdue invoices
            </p>
          </div>

          <div className="glass-card p-6 rounded-xl border border-border/40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Active Revenue</h3>
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold metric-value text-foreground mb-2">
              Rp {((financial?.active_rentals_revenue || 0) / 1000000).toFixed(1)}M
            </p>
            <p className="text-sm text-muted-foreground">
              From {financial?.active_rentals_count || 0} active rentals
            </p>
          </div>
        </div>

        {/* Asset Status Breakdown */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <h3 className="text-lg font-semibold text-foreground mb-4">Asset Status Distribution</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-secondary/5 border border-secondary/20">
              <p className="text-sm text-muted-foreground mb-1">Ready</p>
              <p className="text-2xl font-bold metric-value text-foreground">
                {metrics?.assets?.available || 0}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm text-muted-foreground mb-1">Rented</p>
              <p className="text-2xl font-bold metric-value text-foreground">
                {metrics?.assets?.rented || 0}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
              <p className="text-sm text-muted-foreground mb-1">Maintenance</p>
              <p className="text-2xl font-bold metric-value text-foreground">
                {metrics?.assets?.maintenance || 0}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border">
              <p className="text-sm text-muted-foreground mb-1">Others</p>
              <p className="text-2xl font-bold metric-value text-foreground">
                {(metrics?.assets?.total || 0) - (metrics?.assets?.available || 0) - (metrics?.assets?.rented || 0) - (metrics?.assets?.maintenance || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Anomalies / Alerts */}
        {anomalies.length > 0 && (
          <div className="glass-card p-6 rounded-xl border border-destructive/30">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <h3 className="text-lg font-semibold text-foreground">System Anomalies Detected</h3>
            </div>
            <div className="space-y-3">
              {anomalies.slice(0, 5).map((anomaly, index) => (
                <div
                  key={index}
                  className="p-4 rounded-lg bg-destructive/5 border border-destructive/20"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-foreground mb-1">{anomaly.message}</p>
                      <p className="text-sm text-muted-foreground">
                        Severity: <span className="text-destructive">{anomaly.severity}</span>
                      </p>
                    </div>
                    <span className="px-2 py-1 rounded text-xs font-medium bg-destructive/10 text-destructive">
                      {anomaly.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="glass-card p-6 rounded-xl border border-border/40">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button 
              onClick={() => navigate('/rentals')}
              className="p-4 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors text-left cursor-pointer"
            >
              <p className="font-medium mb-1">New Rental</p>
              <p className="text-xs opacity-80">Create rental</p>
            </button>
            <button 
              onClick={() => navigate('/clients')}
              className="p-4 rounded-lg bg-secondary/10 border border-secondary/30 text-secondary hover:bg-secondary/20 transition-colors text-left cursor-pointer"
            >
              <p className="font-medium mb-1">New Client</p>
              <p className="text-xs opacity-80">Add client</p>
            </button>
            <button 
              onClick={() => navigate('/invoices')}
              className="p-4 rounded-lg bg-primary/10 border border-primary/30 text-primary hover:bg-primary/20 transition-colors text-left cursor-pointer"
            >
              <p className="font-medium mb-1">View Invoices</p>
              <p className="text-xs opacity-80">Check invoices</p>
            </button>
            <button 
              onClick={() => navigate('/assets')}
              className="p-4 rounded-lg bg-secondary/10 border border-secondary/30 text-secondary hover:bg-secondary/20 transition-colors text-left cursor-pointer"
            >
              <p className="font-medium mb-1">View Assets</p>
              <p className="text-xs opacity-80">Browse assets</p>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
